import { FC, useContext, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";

import TextEditor from "../../../../../components/TextEditor";
import { UserContext } from "../../../../../contexts/UserContext";
import sendStandardEvent from "../../../../../hooks/use-firebase-event";
import { pushSuccess } from "../../../../../slices/notifications";
import { useDispatch } from "../../../../../store";
import { copyToClipboard } from "@/lib/clipboard";

const manifests = {
  namespace: `apiVersion: v1
kind: Namespace
metadata:
  name: pomerium-zero
`,
  serviceAccount: `---
apiVersion: v1
kind: ServiceAccount
metadata:
  name: pomerium-impersonate
  namespace: pomerium-zero
`,
  clusterRole: `---
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRole
metadata:
  name: pomerium-impersonate
rules:
  - apiGroups: ['']
    verbs: [impersonate]
    resources: [users]
    resourceNames: [] # add user names here (empty list=any user)
  - apiGroups: ['']
    verbs: [impersonate]
    resources: [groups]
    resourceNames: [] # add group names here (empty list=any group)
`,
  clusterRoleBinding: `---
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRoleBinding
metadata:
  name: pomerium-impersonate
subjects:
  - kind: ServiceAccount
    name: pomerium-impersonate
    namespace: pomerium-zero
roleRef:
  apiGroup: rbac.authorization.k8s.io
  kind: ClusterRole
  name: pomerium-impersonate
`,
  secret: `---
apiVersion: v1
kind: Secret
type: kubernetes.io/service-account-token
metadata:
  name: pomerium-impersonate-token
  namespace: pomerium-zero
  annotations:
    kubernetes.io/service-account.name: "pomerium-impersonate"`,
};

export const ConfigurePermissions: FC = () => {
  const [dryRun, setDryRun] = useState(false);
  const dispatch = useDispatch();
  const { user } = useContext(UserContext);
  const defaultManifestBundle =
    manifests.namespace +
    manifests.serviceAccount +
    manifests.clusterRole +
    manifests.clusterRoleBinding +
    manifests.secret;

  const [rbacManifests, setRbacManifests] = useState(defaultManifestBundle);
  const rbacManifestsChanged = rbacManifests !== defaultManifestBundle;
  const kubectlCommand = `kubectl apply${dryRun ? " --dry-run=server" : ""} -f -`;
  const rbacHeredocCommand = `cat <<EOF | ${kubectlCommand}
${rbacManifests}
EOF`;

  return (
    <Stack direction={"column"} spacing={1}>
      <Typography>
        Use the command below to create a service account in your cluster with
        permissions to impersonate users and groups.
      </Typography>
      <FormControlLabel
        control={
          <Switch
            defaultChecked={dryRun}
            value={dryRun}
            onChange={(_, checked) => {
              setDryRun(checked);
            }}
          />
        }
        label="Dry Run"
      />
      <Box>
        <Box sx={{ bgcolor: "info.alpha8", borderRadius: 2 }}>
          <Stack direction="column" spacing={1} sx={{ m: 1, float: "right" }}>
            <Button
              color="secondary"
              variant="contained"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={async () => {
                await copyToClipboard(rbacHeredocCommand);
                sendStandardEvent("Data was copied", "copying", user, {
                  data: rbacHeredocCommand,
                });
                dispatch(pushSuccess("Copied"));
              }}
            >
              Copy
            </Button>
            {rbacManifestsChanged && (
              <Button
                color="secondary"
                variant="contained"
                size="small"
                startIcon={<RestoreIcon />}
                onClick={() => {
                  setRbacManifests(defaultManifestBundle);
                }}
              >
                Reset
              </Button>
            )}
          </Stack>
          <Typography
            component={"div"}
            onCopyCapture={() => {
              sendStandardEvent("Data was copied", "copying", user, {
                data: rbacHeredocCommand,
              });
            }}
            sx={{
              p: 2,
              fontFamily: "Monospace",
              fontSize: 12,
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
            }}
          >
            <Stack spacing={0}>
              <Typography
                fontFamily={"Droid Sans Mono, monospace"}
                fontSize="14px"
                lineHeight={"18px"}
              >
                {`cat <<EOF | ${kubectlCommand}`}
              </Typography>
              <style>
                {`
                    .monaco-editor, .monaco-editor-background {
                      background-color: transparent;
                    }
                  `}
              </style>
              <TextEditor
                defaultLanguage="yaml"
                defaultValue={defaultManifestBundle}
                value={rbacManifests}
                options={{
                  lineNumbers: "off",
                  glyphMargin: false,
                  folding: false,
                  lineDecorationsWidth: 0,
                  lineNumbersMinChars: 0,
                  theme: "light-transparent",
                  scrollbar: {
                    vertical: "visible",
                  },
                }}
                onChange={(e) => {
                  setRbacManifests(e || "");
                }}
              />

              <Typography
                fontFamily={"Droid Sans Mono, monospace"}
                fontSize="14px"
              >
                {"EOF"}
              </Typography>
            </Stack>
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
};
